import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { runnerApi, type Me } from './runnerApiClient';
import type { NavigationCount } from '../components/SideNavigation';
export type RunnerNavigationCounts = Partial<Record<'today' | 'upcoming' | 'pending' | 'disputes' | 'potential', NavigationCount>>;
export function useRunnerNavigationCounts(me: Me | null): RunnerNavigationCounts {
  const { pathname, search } = useLocation();
  const [state, setState] = useState<{ owner: string; counts: RunnerNavigationCounts }>({ owner: '', counts: {} });
  const owner = me?.runnerId ?? '';
  const canRead = Boolean(me?.capabilities.includes('runner:runs:read'));
  const canMatch = Boolean(me?.capabilities.includes('runner:matching:board'));
  useEffect(() => {
    if (!owner) return;
    let cancelled = false;
    const save = (key: keyof RunnerNavigationCounts, count: number | null, attention = false) => {
      if (!cancelled) setState(old => ({ owner, counts: { ...(old.owner === owner ? old.counts : {}), [key]: { count, attention } } }));
    };
    const read = async (key: keyof RunnerNavigationCounts, get: () => Promise<number | null>, attention = false) => {
      try { save(key, await get(), attention); } catch { save(key, null, attention); }
    };
    const refresh = () => {
      if (canRead) {
        void read('today', async () => { const p = await runnerApi.runs({ view: 'today', limit: 1 }); return p.total ?? (p.hasMore ? null : p.runs.length); });
        void read('upcoming', async () => { const p = await runnerApi.runs({ view: 'upcoming', limit: 1 }); return p.total ?? (p.hasMore ? null : p.runs.length); });
        void read('pending', async () => (await runnerApi.orderAttention('pending')).total, true);
        void read('disputes', async () => (await runnerApi.orderAttention('disputes')).total, true);
      }
      if (canMatch) void read('potential', async () => (await runnerApi.matches('open')).matches.filter(match => match.status === 'requested').length, true);
    };
    refresh();
    const visible = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', visible);
    return () => { cancelled = true; document.removeEventListener('visibilitychange', visible); };
  }, [owner, canRead, canMatch, pathname, search]);
  if (state.owner !== owner) return {};
  return { ...(canRead ? state.counts : {}), potential: canMatch ? state.counts.potential : undefined };
}
